import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import { ImageIndentRight, Stars02 } from '@untitled-ui/icons-react';

import { Button } from '../../../../components/buttons/button';
import { Divider } from '../../../../components/divider';
import { FeaturedIcon } from '../../../../components/featured-icon';
import { SwitchField } from '../../../../components/switch';
import { ToastVariant } from '../../../../components/toast';
import { UserNotificationChannel, UserNotificationEvent } from '../../../../entity/api/user';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { type ErrorAny, mapErrorToTranslation } from '../../../../error/utils';
import { captureException } from '../../../../logger/capture-exception';
import { useAuthUser, useUpdateUser } from '../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../services/translation';
import { useToast } from '../../../../shared/contexts/toast-context';

const t = tNamespace('app.settings.notifications');

export const Route = createFileRoute('/app/settings/_sidebar-settings-layout/notifications')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/settings/_sidebar-settings-layout/notifications',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const addToast = useToast();

  const { data: currentUser } = useAuthUser();
  const updateUserMutation = useUpdateUser();

  const form = useForm({
    defaultValues: {
      email: {
        contentReady:
          currentUser?.notificationPreferences?.events?.[UserNotificationEvent.ArticleGenerated]?.[
            UserNotificationChannel.Email
          ] ?? false,
        whatsNew:
          currentUser?.notificationPreferences?.events?.[UserNotificationEvent.NewsInbox]?.[
            UserNotificationChannel.Email
          ] ?? false,
      },
    },
    onSubmit: function (data) {
      addToast({
        title: t('savingNotifications'),
        variant: ToastVariant.Loading,
        id: Date.now(),
      });

      updateUserMutation
        .mutateAsync({
          notificationPreferences: {
            channels: {
              // TODO: make sure to update these fields when it's implemented
              [UserNotificationChannel.Email]: true,
              [UserNotificationChannel.InApp]: true,
            },
            events: {
              [UserNotificationEvent.ArticleGenerated]: {
                [UserNotificationChannel.Email]: data.value.email.contentReady,
                [UserNotificationChannel.InApp]: data.value.email.contentReady,
              },
              [UserNotificationEvent.NewsInbox]: {
                [UserNotificationChannel.Email]: data.value.email.whatsNew,
                [UserNotificationChannel.InApp]: data.value.email.whatsNew,
              },
            },
          },
        })
        .then(function () {
          addToast({
            title: t('notificationsUpdated'),
            variant: ToastVariant.Success,
            id: Date.now(),
          });
        })
        .catch(function (err: ErrorAny) {
          const errMsg = mapErrorToTranslation(err);
          addToast({
            title: errMsg ?? t('errorSavingNotifications'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'app/settings/_sidebar-settings-layout/notifications.tsx',
              method: 'onSubmit',
              infoMessage: 'unable to update notifications',
            },
            trackError
          );
        });
    },
  });

  return (
    <div className="h-full flex-1 overflow-y-auto">
      <div className="mx-auto mb-0 flex w-full max-w-content flex-col p-4 md:mb-20 md:p-0 md:pt-12">
        <div className="mb-6 flex items-center justify-between gap-4 md:items-start md:gap-0">
          <div className="flex flex-col gap-1">
            <p className="font-display-2">{t('title')}</p>
            <p className="font-body-2-r text-branding-gray">{t('description')}</p>
          </div>
          <Button
            size="md"
            variant="accent"
            onClick={function () {
              void form.handleSubmit();
            }}
            loading={form.state.isSubmitting}
          >
            {tGlobal('save')}
          </Button>
        </div>
        <form>
          <div className="mb-6 flex flex-col gap-6 rounded-lg border border-neutral-100 p-4 md:p-6">
            <p className="font-display-4">{t('emailNotifications')}</p>
            <form.Field
              name="email.contentReady"
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <div className="my-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <FeaturedIcon icon={<Stars02 />} border={false} color="accent" />
                      <div className="flex flex-col gap-1">
                        <p className="font-body-1-sb">{t('contentReadyTitle')}</p>
                        <p className="font-body-2-r">{t('contentReadyDescription')}</p>
                      </div>
                    </div>
                    <SwitchField
                      checked={field.state.value}
                      label={null as unknown as string}
                      onCheckedChange={function (checked) {
                        field.handleChange(checked);
                      }}
                    />
                  </div>
                );
              }}
            />
            <Divider className="px-6" />
            <form.Field
              name="email.whatsNew"
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <div className="my-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <FeaturedIcon icon={<ImageIndentRight />} border={false} color="accent" />
                      <div className="flex flex-col gap-1">
                        <p className="font-body-1-sb">{t('whatsNewTitle')}</p>
                        <p className="font-body-2-r">{t('whatsNewDescription')}</p>
                      </div>
                    </div>
                    <SwitchField
                      label={null as unknown as string}
                      checked={field.state.value}
                      onCheckedChange={function (checked) {
                        field.handleChange(checked);
                      }}
                    />
                  </div>
                );
              }}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
